/**
 * "Today" — the session deck.
 *
 * Big swipe cards, filters always available: by domain (always) and by person
 * tag (when any question carries one). Buttons mirror the gestures.
 */

import { useState } from "react";
import type { NewsEntry } from "../lib/news";
import type { Person } from "../lib/persons";
import { personLabel } from "../lib/persons";
import type { Question } from "../lib/questions";
import { filterByPersons } from "../lib/session";
import { CompactCard } from "./CompactCard";
import { NewsCard } from "./NewsCard";
import { TagSheet } from "./TagSheet";

export function Today({
  deck,
  totalOpen,
  laterCount,
  hiddenByModes,
  hiddenDomains,
  newsLatest,
  newsUnseen,
  onOpenNews,
  dispatching,
  persons,
  stagedPaths,
  onOpenQuestion,
  onSkip,
  onShowLater,
  onTagQuestion,
  onDispatch,
  onRefresh,
}: {
  deck: Question[];
  totalOpen: number;
  /** Questions the founder set to "Later" this sitting (skipped set). */
  laterCount: number;
  /** Open questions hidden from the deck by quiet area modes (Pause / Never ask / Answer for me). */
  hiddenByModes: number;
  /** The domains those hidden questions live in. */
  hiddenDomains: string[];
  /** Newest repo-news digest (gold card on top) — null when none exists yet. */
  newsLatest: NewsEntry | null;
  /** True when that digest has not been opened yet. */
  newsUnseen: boolean;
  onOpenNews: () => void;
  dispatching: boolean;
  persons: Person[];
  stagedPaths: ReadonlySet<string>;
  onOpenQuestion: (q: Question) => void;
  onSkip: (q: Question) => void;
  onShowLater: () => void;
  onTagQuestion: (q: Question, ids: string[]) => void;
  onDispatch: () => void;
  onRefresh: () => void;
}) {
  const [domainFilter, setDomainFilter] = useState<Set<string>>(new Set());
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [sheetFor, setSheetFor] = useState<Question | null>(null);

  const domainOptions = [...new Set(deck.map((q) => q.domain))].sort();
  const tagged = new Set(deck.flatMap((q) => q.persons));

  let filtered = deck;
  if (domainFilter.size > 0) filtered = filtered.filter((q) => domainFilter.has(q.domain));
  filtered = filterByPersons(filtered, tagFilter);
  const current = filtered[0];

  const anyFilter = domainFilter.size > 0 || tagFilter.size > 0;
  const clearFilters = () => {
    setDomainFilter(new Set());
    setTagFilter(new Set());
  };
  const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
    const nextSet = new Set(set);
    if (nextSet.has(id)) nextSet.delete(id);
    else nextSet.add(id);
    apply(nextSet);
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-100">Deck</h1>
        <span className="flex items-center gap-1.5">
          {newsLatest && (
            <button
              onClick={onOpenNews}
              className="chip bg-gold/15 text-gold"
              title="Repo news — what changed and what waits"
            >
              📰 News{newsUnseen ? " ●" : ""}
            </button>
          )}
          <button onClick={onRefresh} className="btn-ghost px-3 py-1.5 text-xs">
            ⟳ Refresh
          </button>
        </span>
      </div>

      {newsUnseen && newsLatest && (
        <div className="mb-3">
          <NewsCard entry={newsLatest} onOpen={onOpenNews} />
        </div>
      )}

      {domainOptions.length > 0 && totalOpen > 0 && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-ink-600">Filter:</span>
          <button
            onClick={clearFilters}
            className={`chip ${!anyFilter ? "bg-accent/15 text-accent-soft" : "bg-ink-850 text-ink-400"}`}
          >
            All
          </button>
          {domainOptions.map((d) => (
            <button
              key={d}
              onClick={() => toggle(domainFilter, d, setDomainFilter)}
              className={`chip ${
                domainFilter.has(d) ? "bg-accent/15 text-accent-soft" : "bg-ink-850 text-ink-400"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {tagged.size > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {[...tagged].sort().map((id) => (
            <button
              key={id}
              onClick={() => toggle(tagFilter, id, setTagFilter)}
              className={`chip ${
                tagFilter.has(id) ? "bg-accent/15 text-accent-soft" : "bg-ink-850 text-ink-400"
              }`}
            >
              {personLabel(persons, id) ?? id}
            </button>
          ))}
        </div>
      )}

      <div className="mb-3 text-sm text-ink-500">
        {totalOpen > 0 ? (
          <>
            <span className="font-medium text-accent-soft">{totalOpen}</span> open question
            {totalOpen === 1 ? "" : "s"}
            {anyFilter && <> · showing {filtered.length}</>}
          </>
        ) : (
          "No open questions."
        )}
      </div>

      {laterCount > 0 && (
        <div className="mb-3">
          <button
            onClick={onShowLater}
            className="chip bg-ink-850 text-ink-400 hover:text-ink-200"
            title="Bring every question you set to Later back into the deck"
          >
            ↺ {laterCount} set to Later — bring back
          </button>
        </div>
      )}

      {hiddenByModes > 0 && (
        <div className="mb-3 text-xs leading-relaxed text-ink-500">
          {hiddenByModes} question{hiddenByModes === 1 ? "" : "s"} hidden by area modes (
          {hiddenDomains.join(", ")}) — Pause / Never ask / Answer for me keep a domain out of
          the Deck. Change the modes under Questions → domain.
        </div>
      )}

      {current ? (
        <>
          <CompactCard
            q={current}
            persons={persons}
            staged={stagedPaths.has(current.path)}
            onOpen={() => onOpenQuestion(current)}
            onSkip={() => onSkip(current)}
            onTag={() => setSheetFor(current)}
          />
          <div className="mt-3 flex justify-center gap-2">
            <button className="btn-ghost px-4 py-2 text-xs" onClick={() => onSkip(current)}>
              Later
            </button>
            <button
              className="btn-ghost px-4 py-2 text-xs"
              onClick={() => setSheetFor(current)}
            >
              Tags
            </button>
            <button
              className="btn-primary px-4 py-2 text-xs"
              onClick={() => onOpenQuestion(current)}
            >
              Open
            </button>
          </div>
        </>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-lg font-medium text-ink-100">
            {anyFilter && deck.length > 0
              ? "Nothing under this filter."
              : laterCount > 0
                ? "All cards set to Later."
                : "All answered."}
          </div>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink-400">
            {anyFilter && deck.length > 0
              ? "Clear the filters to see the whole stack."
              : laterCount > 0
                ? "You skipped them this sitting — nothing is lost, bring them back anytime."
                : totalOpen > 0
                  ? "Some questions are snoozed or paused — refresh to see them again."
                  : "The tree is quiet. A run folds your answers in and may ask the next questions — trigger one whenever you like."}
          </p>
          {anyFilter && deck.length > 0 ? (
            <button className="btn-ghost mx-auto mt-4 px-4 py-2 text-xs" onClick={clearFilters}>
              Clear filters
            </button>
          ) : (
            <div className="mt-4 flex flex-col items-center gap-2">
              {laterCount > 0 && (
                <button className="btn-primary px-4 py-2 text-xs" onClick={onShowLater}>
                  ↺ Bring all {laterCount} back
                </button>
              )}
              <button
                className="btn-run px-4 py-2 text-xs"
                disabled={dispatching}
                onClick={onDispatch}
              >
                {dispatching ? "starting…" : "Start a run now"}
              </button>
            </div>
          )}
        </div>
      )}

      {sheetFor && (
        <TagSheet
          persons={persons}
          selected={sheetFor.persons}
          onApply={(ids) => {
            setSheetFor(null);
            onTagQuestion(sheetFor, ids);
          }}
          onClose={() => setSheetFor(null)}
        />
      )}
    </div>
  );
}
